import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: 'ri-map-pin-2-line',
    title: 'Deep Local Expertise',
    desc: 'We know Bucks, Montgomery, Chester, and Delaware County real estate inside and out — from county recording offices to local market nuances.',
  },
  {
    icon: 'ri-timer-flash-line',
    title: 'Fast, On-Time Closings',
    desc: 'We work efficiently to keep your transaction on schedule, coordinating with all parties to ensure a smooth closing day.',
  },
  {
    icon: 'ri-chat-check-line',
    title: 'Clear Communication',
    desc: "You'll always know where your transaction stands. We keep buyers, sellers, and agents informed every step of the way.",
  },
  {
    icon: 'ri-shield-check-line',
    title: 'Comprehensive Protection',
    desc: 'From thorough title searches to robust title insurance policies, we protect your investment from hidden risks and defects.',
  },
];

const AboutPage = () => {
  return (
    <>
      <head>
        <title>About Black Dog Settlement | Title Company Newtown Bucks County PA</title>
        <meta name="description" content="Meet the founders of Black Dog Settlement — Gina Spaziano and Justin Lucci. Experienced real estate professionals serving Bucks, Montgomery, Chester, and Delaware County, PA." />
        <meta name="keywords" content="about Black Dog Settlement, title company Newtown PA, Gina Spaziano, Justin Lucci, settlement services Bucks County" />
        <link rel="canonical" href="https://www.blackdogsettlement.com/about" />
        <meta name="geo.region" content="US-PA" />
        <meta name="geo.placename" content="Newtown, Pennsylvania" />
        <link href="https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@600;700;800;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />
      </head>
      <div style={{ fontFamily: "'Inter', sans-serif" }}>
        <Navbar />

        {/* Hero */}
        <section className="relative w-full min-h-[60vh] bg-[#0f0f0f] flex flex-col justify-end px-8 md:px-16 lg:px-24 pt-40 pb-16 overflow-hidden">
          {/* Background subtle texture */}
          <div className="absolute inset-0 opacity-10"
            style={{ backgroundImage: 'repeating-linear-gradient(45deg, #c9a84c 0, #c9a84c 1px, transparent 0, transparent 50%)', backgroundSize: '20px 20px' }}>
          </div>
          <div className="relative z-10 max-w-4xl">
            {/* Logo */}
            <img
              src="https://storage.readdy-site.link/project_files/24cbc93a-97c5-4a4c-8795-e638165a0b41/150f0a6f-f0eb-4598-a9c6-5fbc1a938f2f_black-dog-settlement-services-white.png?v=f6ded5401678ec51453df7ea2bb5dc1a"
              alt="Black Dog Settlement Services"
              className="h-16 w-auto object-contain mb-10"
            />
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-[#c9a84c]"></div>
              <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">About Us</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-black text-white leading-tight mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Experience<br />You Can <span className="text-[#c9a84c]">Trust.</span>
            </h1>
            <p className="text-white/40 text-sm leading-relaxed max-w-xl">
              Black Dog Settlement is a locally owned title and settlement company founded by two of southeastern Pennsylvania's most respected real estate professionals — dedicated to making every closing smooth, transparent, and stress-free.
            </p>
          </div>
        </section>

        {/* Stats bar */}
        <section className="bg-[#c9a84c]">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-[#b8963e]">
              {[
                { val: '500+', label: 'Closings Completed' },
                { val: '$500M+', label: 'Real Estate Closed' },
                { val: '4', label: 'Counties Served' },
                { val: '15+', label: 'Years Combined' },
              ].map((s) => (
                <div key={s.label} className="py-8 px-6 text-center">
                  <p className="text-3xl font-black text-[#0f0f0f]" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{s.val}</p>
                  <p className="text-[#0f0f0f]/60 text-xs uppercase tracking-widest mt-1 font-semibold">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Our Story */}
        <section className="bg-white py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-[#c9a84c]"></div>
              <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">Our Story</span>
            </div>
            <div className="flex flex-col lg:flex-row gap-16 items-start">
              <div className="w-full lg:w-[55%]">
                <h2 className="text-4xl md:text-5xl font-black text-[#0f0f0f] leading-tight mb-8" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                  Built on Local Knowledge<br />&amp; Integrity
                </h2>
                <div className="space-y-4 text-stone-500 text-sm leading-relaxed">
                  <p>
                    Black Dog Settlement was born from a simple truth: the best settlement company is built by people who have sat on the other side of the table. Founders Gina Spaziano and Justin Lucci spent years as top-producing real estate professionals in Bucks, Montgomery, Chester, and Delaware Counties — closing hundreds of transactions and guiding clients through every complexity the Pennsylvania market could offer.
                  </p>
                  <p>
                    After witnessing firsthand how critical — and how often overlooked — the title and settlement process is, they made a decision: bring the same client-first philosophy they built their real estate careers on to the settlement side of every deal. The result is Black Dog Settlement, a company where every closing is handled with the precision, communication, and care that buyers, sellers, and agents deserve.
                  </p>
                  <p>
                    Based in Newtown, PA, we serve the entire southeastern Pennsylvania region with deep knowledge of county recording requirements, local market nuances, and the real estate relationships that make transactions move smoothly from contract to keys.
                  </p>
                </div>
              </div>
              <div className="w-full lg:w-[45%] flex flex-col gap-6">
                <div className="bg-[#f7f5f0] p-8 border-l-4 border-[#c9a84c]">
                  <p className="text-[#0f0f0f] text-lg font-bold italic leading-relaxed" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                    "We've been on both sides of the closing table. We know what agents need, what buyers feel, and what sellers expect. That experience is what makes Black Dog different."
                  </p>
                  <p className="text-[#c9a84c] text-xs font-bold tracking-widest uppercase mt-4">— Gina &amp; Justin, Founders</p>
                </div>
                <div className="grid grid-cols-2 gap-0 border border-stone-100">
                  {[
                    { val: 'Newtown, PA', label: 'Headquarters' },
                    { val: 'SE Pennsylvania', label: 'Service Region' },
                    { val: '215-499-0796', label: 'Direct Line' },
                    { val: 'Mon–Fri 9–5', label: 'Office Hours' },
                  ].map((item, i) => (
                    <div key={item.label} className={`p-5 ${i % 2 === 0 ? 'border-r border-stone-100' : ''} ${i < 2 ? 'border-b border-stone-100' : ''}`}>
                      <p className="text-[#0f0f0f] font-bold text-sm">{item.val}</p>
                      <p className="text-stone-400 text-xs uppercase tracking-widest mt-0.5">{item.label}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Mobile-only float override for founder headshots */}
        <style>{`
          @media (max-width: 767px) {
            .founder-row {
              display: block !important;
            }
            .founder-image-wrap {
              float: right !important;
              width: 42% !important;
              margin-left: 14px !important;
              margin-bottom: 8px !important;
            }
            .founder-text-wrap::after {
              content: '';
              display: table;
              clear: both;
            }
          }
        `}</style>

        {/* Meet the Founders */}
        <section className="bg-[#0f0f0f] py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">

            {/* Section header */}
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-[#c9a84c]"></div>
              <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">Meet the Founders</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white leading-tight mb-16" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              The People Behind<br />Every Closing
            </h2>

            {/* ONE container — TWO rows */}
            <div className="flex flex-col gap-20">

              {/* Row 1: Gina — flex-row: image left, text right */}
              <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'flex-start', gap: '48px' }}
                className="founder-row">
                {/* Image */}
                <div style={{ width: '35%', flexShrink: 0 }} className="founder-image-wrap">
                  <img
                    src="https://storage.readdy-site.link/project_files/24cbc93a-97c5-4a4c-8795-e638165a0b41/f02e1465-5c22-482c-a134-e4e40d4008f0_Gina-Spaziano-Image.png?v=f54a9b4a4187667daf54fee95ccd0ca2"
                    alt="Gina Spaziano - Co-Founder Black Dog Settlement"
                    style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '8px' }}
                  />
                </div>
                {/* Text */}
                <div style={{ flex: 1 }} className="founder-text-wrap">
                  <p className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase mb-2">Co-Founder</p>
                  <h3 className="text-white text-4xl font-black mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Gina Spaziano</h3>
                  <div className="space-y-4 text-white/50 text-sm leading-relaxed">
                    <p>Gina Spaziano is one of southeastern Pennsylvania's most accomplished real estate professionals, with over $500 million in closed transactions and a reputation built on personalized service, deep market knowledge, and an unwavering commitment to her clients.</p>
                    <p>A lifelong advocate for the communities of Bucks, Montgomery, Chester, and Delaware Counties, Gina spent years as a top-producing agent and broker — earning recognition as a Home Builder Sales Manager of the Year, K. Hovnanian Sales Manager of the Year, and a Top Producer in Bucks County. Her ability to turn complex real estate transactions into seamless, meaningful experiences for her clients set her apart in a competitive market.</p>
                    <p>Recognizing that the settlement process was the final — and often most stressful — chapter of every transaction, Gina co-founded Black Dog Settlement to bring the same level of care, communication, and expertise to the closing table that she had always delivered as an agent.</p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {['$500M+ Closed', 'Top Producer Bucks County', 'K. Hovnanian Award', '360+ Transactions'].map((tag) => (
                      <span key={tag} className="text-xs border border-white/10 text-white/40 px-3 py-1 whitespace-nowrap">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

              {/* Row 2: Justin — flex-row-reverse: text left, image right */}
              <div style={{ display: 'flex', flexDirection: 'row-reverse', alignItems: 'flex-start', gap: '48px' }}
                className="founder-row">
                {/* Image */}
                <div style={{ width: '35%', flexShrink: 0 }} className="founder-image-wrap">
                  <img
                    src="https://storage.readdy-site.link/project_files/24cbc93a-97c5-4a4c-8795-e638165a0b41/ed7783d6-851b-41fb-a960-f78dd3b57179_Justin-Lucci-Image.jpg?v=ca6acf68891ffe8e3c1430076d30a5ed"
                    alt="Justin Lucci - Co-Founder Black Dog Settlement"
                    style={{ width: '100%', height: 'auto', display: 'block', borderRadius: '8px' }}
                  />
                </div>
                {/* Text */}
                <div style={{ flex: 1 }} className="founder-text-wrap">
                  <p className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase mb-2">Co-Founder</p>
                  <h3 className="text-white text-4xl font-black mb-6" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Justin Lucci</h3>
                  <div className="space-y-4 text-white/50 text-sm leading-relaxed">
                    <p>Justin Lucci is a lifelong Bucks County resident whose passion for real estate is matched only by his dedication to the clients he serves. As a top real estate professional specializing in buying and selling throughout Bucks County and the surrounding region, Justin built his career on a foundation of straightforward advice, exceptional service, and results that consistently exceed expectations.</p>
                    <p>Before real estate, Justin honed his entrepreneurial instincts in the car business — running a successful venture in luxury, exotic, and premium vehicles. That background gave him a sharp business acumen, a mastery of negotiation, and a client-service philosophy that translated directly into his real estate practice. Known for making the process smooth and effortless, Justin became the agent buyers and sellers trusted to get the deal done right.</p>
                    <p>Co-founding Black Dog Settlement was a natural evolution — bringing his expertise in transactions, client relationships, and operational excellence to the settlement side of every deal, ensuring every closing reflects the same standard he set as an agent.</p>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-6">
                    {['Bucks County Expert', 'Buyer & Seller Specialist', 'Newtown PA Based', 'Entrepreneur'].map((tag) => (
                      <span key={tag} className="text-xs border border-white/10 text-white/40 px-3 py-1 whitespace-nowrap">{tag}</span>
                    ))}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* Values */}
        <section className="bg-[#f7f5f0] py-24">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-8 h-px bg-[#c9a84c]"></div>
              <span className="text-[#c9a84c] text-xs font-bold tracking-[0.3em] uppercase">Why Choose Us</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-[#0f0f0f] leading-tight mb-14" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Why Black Dog Settlement?
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-stone-200">
              {values.map((v) => (
                <div key={v.title} className="border-b border-r border-stone-200 p-8 group hover:bg-[#0f0f0f] transition-colors duration-300">
                  <div className="w-12 h-12 flex items-center justify-center border border-stone-200 group-hover:border-[#c9a84c] mb-6 transition-colors">
                    <i className={`${v.icon} text-xl text-[#c9a84c]`}></i>
                  </div>
                  <h3 className="font-bold text-[#0f0f0f] group-hover:text-white text-base mb-3 transition-colors">{v.title}</h3>
                  <p className="text-stone-500 group-hover:text-white/40 text-sm leading-relaxed transition-colors">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#0f0f0f] py-20">
          <div className="max-w-7xl mx-auto px-6 md:px-12 flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-4xl font-black text-white mb-3" style={{ fontFamily: "'Cormorant Garamond', serif" }}>Ready to Get Started?</h2>
              <p className="text-white/40 text-sm">Contact Black Dog Settlement for your Bucks, Montgomery, Chester, or Delaware County closing.</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <a
                href="tel:2154990796"
                className="inline-flex items-center justify-center gap-2 bg-[#c9a84c] hover:bg-[#b8963e] text-[#0f0f0f] font-bold px-8 py-4 text-xs tracking-[0.15em] uppercase whitespace-nowrap cursor-pointer transition-colors"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-phone-line"></i>
                </div>
                Call 215-499-0796
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/50 text-white font-bold px-8 py-4 text-xs tracking-[0.15em] uppercase whitespace-nowrap cursor-pointer transition-colors"
              >
                Send a Message
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default AboutPage;
